import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { CookieSettingsButton } from "@/components/CookieConsent";
import { ArrowLeft, Mail } from "lucide-react";
import { currentBrand } from "@/lib/brand";
import { useAuth } from "@/hooks/use-auth";

export const Route = createFileRoute("/cookie-policy")({
  component: CookiePolicyPage,
  head: () => ({
    meta: [
      { title: "Cookie Policy" },
      { name: "description", content: "How we use cookies and how to manage your preferences." },
      { property: "og:title", content: "Cookie Policy" },
      {
        property: "og:description",
        content: "How we use cookies and how to manage your preferences.",
      },
      { property: "og:url", content: "/cookie-policy" },
    ],
    links: [{ rel: "canonical", href: "/cookie-policy" }],
  }),
});

function CookiePolicyPage() {
  const { profile } = useAuth();
  const backLink = profile
    ? profile.role === "staff" || profile.role === "admin"
      ? "/app/staff/today"
      : "/app/customer/dashboard"
    : "/";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border bg-background">
        <div className="mx-auto max-w-6xl flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <img
              src={currentBrand.logoSrc}
              alt={`${currentBrand.name} logo`}
              className="h-8 w-auto"
            />
            <h1 className="text-lg font-semibold">{currentBrand.name}</h1>
          </div>
          <div className="flex items-center gap-3">
            <CookieSettingsButton />
            <Link
              to={backLink}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {profile ? "Back to app" : "Back to home"}
            </Link>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-3xl flex-1 px-6 pt-10 pb-20">
        <h1 className="text-4xl font-bold tracking-tight">Cookie Policy</h1>
        <p className="mt-3 text-muted-foreground">
          This page explains how we use cookies and similar technologies on this site, and how you
          can control them.
        </p>

        <section className="mt-10 space-y-3">
          <h2 className="text-xl font-semibold">What are cookies?</h2>
          <p className="text-sm text-muted-foreground">
            Cookies are small text files stored on your device when you visit a website. They are
            widely used to make sites work, or work more efficiently, and to provide information to
            site owners.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">Categories we use</h2>
          <div className="space-y-3">
            <Category
              title="Strictly necessary"
              body="Required for the site to function — for example to keep you signed in, remember your cart, and protect against fraud. These cannot be turned off."
            />
            <Category
              title="Analytics"
              body="Help us understand how visitors use the site so we can improve it. Only set if you allow them."
            />
            <Category
              title="Marketing"
              body="Used to measure marketing performance and, where applicable, deliver relevant content. Only set if you allow them."
            />
          </div>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">Managing your preferences</h2>
          <p className="text-sm text-muted-foreground">
            You can change your choices at any time by opening{" "}
            <CookieSettingsButton className="underline underline-offset-2 hover:text-foreground" />.
            You can also clear cookies in your browser settings.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">Contact</h2>
          <p className="text-sm text-muted-foreground">
            For questions about this policy, please contact our support team via the Help Center.
          </p>
        </section>
      </main>

      <footer className="bg-sky-50 dark:bg-sky-950/20 py-8 border-t border-border">
        <div className="mx-auto max-w-6xl px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img
                src={currentBrand.logoSrc}
                alt={`${currentBrand.name} logo`}
                className="h-6 w-auto"
              />
              <span className="text-sm font-medium">{currentBrand.name}</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground transition-colors">
                Home
              </Link>
              <Link to="/pricing" className="hover:text-foreground transition-colors">
                Pricing
              </Link>
              <a
                href="mailto:support@resoluteink.co.uk"
                className="flex items-center gap-1 hover:text-foreground transition-colors"
              >
                <Mail className="h-4 w-4" /> Support
              </a>
            </div>
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} {currentBrand.name}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Category({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-border p-4">
      <p className="font-medium">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}
